import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Search } from 'lucide-react';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import CustomerService from '@/api/customers.service';
import countries from './Countries';
import { Textarea } from './ui/textarea';
import { useToast } from '@/hooks/use-toast';

type Customer = {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  street: string;
  city: string;
  state: string;
  zipCode?: string;
  country: string;
  companyName?: string;
};

type BillToSectionProps = {
  customerDetails: Customer;
  setCustomerDetails: React.Dispatch<React.SetStateAction<Customer>>;
  customerData: Customer[];
  setCustomerData: React.Dispatch<React.SetStateAction<Customer[]>>;
};

const BillToSection: React.FC<BillToSectionProps> = ({ customerDetails, setCustomerDetails, customerData, setCustomerData }) => {
  const { toast } = useToast();
  const [searchCustomer, setSearchCustomer] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [customerSelected, setCustomerSelected] = useState(false);
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);

  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  useEffect(() => {
    if (searchCustomer.length > 0) {
      const delayDebounceFn = setTimeout(() => {
        handleSearchCustomer();
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchCustomer]);

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  const handleSearchCustomer = async () => {
    setIsSearching(true);
    try {
      const response = await CustomerService.searchCustomer(selectedBusiness.id, searchCustomer);
      const { data } = response;
      if (data.result === 'success') {
        setCustomerData(data.customers);
      } else {
        showToastWithTimeout("error", "Failed to search customer", data.message, 3000);
        setCustomerData([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddCustomer = async () => {
    setIsAddingCustomer(true);
    try {
      const response = await CustomerService.addCustomer(selectedBusiness.id, customerDetails);
      const { data } = response;
      if (data.result === 'success') {
        setCustomerData(prev => [...prev, { ...customerDetails, id: data.customerId }]);
        setCustomerDetails({ ...customerDetails, id: data.customerId });
        setCustomerSelected(true);
        showToastWithTimeout("default", "✅ Success", "Customer added successfully", 3000);
      } else {
        showToastWithTimeout("error", "❌ Error", data.message || "Failed to add customer", 3000);
      }
    } catch (error) {
      console.error(error);
      showToastWithTimeout("error", "❌ Error", "An error occurred while adding the customer", 3000);
    } finally {
      setIsAddingCustomer(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Bill To:</h2>
      <div className='flex items-center my-4'>
        <Input
          type="text"
          placeholder="Search by Customer Name"
          value={searchCustomer}
          onChange={(e) => setSearchCustomer(e.target.value)}
          className="mr-2"
        />
        <Button onClick={handleSearchCustomer} disabled={isSearching}>
          {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
      </div>
      {customerData.length > 0 && (
        <select
          onChange={(e) => {
            const selectedCustomer = customerData.find((customer: Customer) => customer.id === Number(e.target.value));
            if (selectedCustomer) {
              setCustomerDetails(selectedCustomer);
              setCustomerSelected(true);
            }
          }}
          className="w-full p-2 border rounded-lg bg-white shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="" disabled selected>Select a customer</option>
          {customerData.map((customer: Customer) => (
            <option
              key={customer.id}
              value={String(customer.id)}
              className="p-2 border-b border-gray-200 hover:bg-gray-100"
            >
              {customer.companyName} - {customer.firstName} {customer.lastName}
            </option>
          ))}
        </select>
      )}
      {!customerSelected && (
        <>
          <Input
            type="text"
            placeholder="Company Name"
            className="w-full p-2 border rounded"
            value={customerDetails.companyName}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                companyName: e.target.value,
              }))
            }
          />
          <Input
            type="text"
            placeholder="First Name"
            className="w-full p-2 border rounded"
            value={customerDetails.firstName}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                firstName: e.target.value,
              }))
            }
          />
          <Input
            type="text"
            placeholder="Last Name"
            className="w-full p-2 border rounded"
            value={customerDetails.lastName}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                lastName: e.target.value,
              }))
            }
          />
          <Input
            type="text"
            placeholder="Street"
            className="w-full p-2 border rounded"
            value={customerDetails.street}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                street: e.target.value,
              }))
            }
          />
          <Input
            type="text"
            placeholder="City"
            className="w-full p-2 border rounded"
            value={customerDetails.city}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                city: e.target.value,
              }))
            }
          />
          <Input
            type="text"
            placeholder="State"
            className="w-full p-2 border rounded"
            value={customerDetails.state}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                state: e.target.value,
              }))
            }
          />
          <Input
            type="text"
            placeholder="Zip Code"
            className="w-full p-2 border rounded"
            value={customerDetails.zipCode}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                zipCode: e.target.value,
              }))
            }
          />
          <Select
            value={customerDetails.country}
            onValueChange={(value) =>
              setCustomerDetails(prev => ({
                ...prev,
                country: value,
              }))
            }
          >
            <SelectTrigger className="w-full p-2 border rounded">
              <SelectValue placeholder="Select Country" />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country: string) => (
                <SelectItem key={country} value={country}>
                  {country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="email"
            placeholder="Email Address"
            className="w-full p-2 border rounded"
            value={customerDetails.emailAddress}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                emailAddress: e.target.value,
              }))
            }
          />
          <Input
            type="tel"
            placeholder="Phone Number"
            className="w-full p-2 border rounded"
            value={customerDetails.phoneNumber}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                phoneNumber: e.target.value,
              }))
            }
          />
          <Button onClick={handleAddCustomer} className="w-full bg-blue-600 text-white mt-4">
            {isAddingCustomer ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Customer"}
          </Button>
        </>
      )}
      {customerSelected && (
        <div className="space-y-2">
          <Input
            type="text"
            placeholder="Company Name"
            className="w-full p-2 border rounded"
            value={customerDetails.companyName}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                companyName: e.target.value,
              }))
            }
          />
          <Input
            type="text"
            placeholder="First Name"
            className="w-full p-2 border rounded"
            value={customerDetails.firstName}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                firstName: e.target.value,
              }))
            }
          />
          <Input
            type="text"
            placeholder="Last Name"
            className="w-full p-2 border rounded"
            value={customerDetails.lastName}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                lastName: e.target.value,
              }))
            }
          />
          <Textarea
            placeholder="Street"
            className="w-full p-2 border rounded"
            value={customerDetails.street}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                street: e.target.value,
              }))
            }
          />
          <Input
            type="email"
            placeholder="Email Address"
            className="w-full p-2 border rounded"
            value={customerDetails.emailAddress}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                emailAddress: e.target.value,
              }))
            }
          />
          <Input
            type="tel"
            placeholder="Phone Number"
            className="w-full p-2 border rounded"
            value={customerDetails.phoneNumber}
            onChange={(e: any) =>
              setCustomerDetails(prev => ({
                ...prev,
                phoneNumber: e.target.value,
              }))
            }
          />
        </div>
      )}
    </div>
  );
};

export default BillToSection; 